import styled from "styled-components"
import Header from "../../Common/Header"
import Footer from "../../Common/Footer"
import { futuristicListMarker } from "@components/base/listMarkers"

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 6vw, 4rem);
  background: #f5f8fb;
`

const Hero = styled.section`
  position: relative;
  min-height: min(720px, 76vh);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(5rem, 10vw, 7rem) 1.5rem;
  color: #ffffff;
  background: url("/img/bg.jpg") center/cover no-repeat;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(130deg, rgba(10, 33, 61, 0.92) 15%, rgba(16, 74, 126, 0.72) 55%, rgba(18, 105, 170, 0.62) 100%);
    z-index: 0;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(1080px, 92vw);
  display: grid;
  gap: 1.5rem;
  text-align: center;
  backdrop-filter: blur(6px);
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.3rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.18);
  letter-spacing: 0.12em;
  font-size: 0.78rem;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.6rem, 6vw, 4rem);
  line-height: 1.1;
  color: #fff;
`

const HeroSubtitle = styled.p`
  margin: 0 auto;
  max-width: 760px;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
`

const Article = styled.article`
  width: min(960px, 92vw);
  margin: 0 auto;
  background: #ffffff;
  border-top: 4px solid #337abd;
  border-radius: 2px;
  padding: clamp(2rem, 5vw, 3rem);
  box-shadow: 0 24px 60px rgba(13, 65, 114, 0.12);
  display: grid;
  gap: 1.6rem;
  color: #1b2e41;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  color: #103c68;
`

const Paragraph = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.8;
`

const BulletList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.8rem;
  font-size: 1.05rem;
  line-height: 1.7;

  li {
    ${futuristicListMarker};
    color: #1b2e41;
  }
`

const Quote = styled.blockquote`
  margin: 0;
  padding: 1.2rem 1.4rem;
  border-left: 4px solid ${({ theme }) => theme.primary};
  background: rgba(27, 117, 188, 0.08);
  font-style: italic;
  color: #103c68;
`

const ReglementationImmeublesPortneuf = () => {
  return (
    <PageWrapper>
      <Header />
      <main id="contenu-principal">
        <Hero>
          <HeroContent>
            <HeroBadge>Repères pour propriétaires</HeroBadge>
            <HeroTitle>Règles et échéances à suivre pour un immeuble à revenus</HeroTitle>
            <HeroSubtitle>
              Un point de départ pratique pour organiser les baux, la sécurité, l’entretien et les autorisations municipales.
            </HeroSubtitle>
          </HeroContent>
        </Hero>

        <Article>
          <Paragraph>
            Les règles applicables varient selon le type d’immeuble, les travaux et la municipalité. Ce guide ne remplace pas un avis juridique : il
            présente les vérifications de base et renvoie aux sources officielles à consulter avant d’agir.
          </Paragraph>

          <SectionTitle>1. Baux, renouvellements et avis</SectionTitle>
          <Paragraph>
            Le Tribunal administratif du logement précise les délais et le contenu des avis de modification du bail. Conservez les baux, les preuves
            de transmission, les réponses des locataires et les pièces utilisées pour vos calculs. Avant d’envoyer un avis, consultez la page
            officielle sur{" "}
            <a
              href="https://www.tal.gouv.qc.ca/fr/reconduction-du-bail-et-fixation-de-loyer/modification-d-une-condition-du-bail"
              target="_blank"
              rel="noopener noreferrer"
            >
              la reconduction du bail et la fixation du loyer
            </a>
            .
          </Paragraph>

          <SectionTitle>2. Sécurité et entretien</SectionTitle>
          <Paragraph>
            Les municipalités peuvent adopter des exigences sur la salubrité, les nuisances et les équipements de sécurité. Vérifiez le règlement de
            la ville où se trouve l’immeuble, puis consignez les inspections et remplacements effectués. Le gouvernement du Québec présente le cadre
            de ces pouvoirs dans son guide sur les{` `}
            <a
              href="https://www.quebec.ca/habitation-territoire/amenagement-developpement-territoires/amenagement-territoire/guide-prise-decision-urbanisme/protection-environnement/reglements-environnement-salubrite-securite-nuisances"
              target="_blank"
              rel="noopener noreferrer"
            >
              règlements municipaux de salubrité, de sécurité et de nuisances
            </a>
            .
          </Paragraph>
          <BulletList>
            <li>Vérifier les avertisseurs de fumée et de monoxyde de carbone selon les exigences applicables.</li>
            <li>Inspecter les issues, garde-corps, espaces communs et systèmes mécaniques.</li>
            <li>Documenter les signalements de salubrité, les interventions et les communications aux occupants.</li>
          </BulletList>

          <SectionTitle>3. Travaux, permis et professionnels</SectionTitle>
          <Paragraph>
            Avant de modifier un bâtiment, confirmez auprès de la municipalité si un permis ou un certificat est requis. Certains travaux doivent
            aussi être réalisés par un entrepreneur ou un professionnel qualifié. Les règles peuvent être plus strictes pour un immeuble patrimonial
            ou situé dans un secteur protégé.
          </Paragraph>

          <Quote as="aside">
            Bon réflexe : validez toujours l’information auprès du Tribunal administratif du logement, de votre municipalité et du professionnel
            compétent. Une pratique utilisée dans une ville ou pour un type d’immeuble ne s’applique pas nécessairement ailleurs.
          </Quote>

          <SectionTitle>4. Préparer votre dossier de conformité</SectionTitle>
          <Paragraph>Nous recommandons de tenir un dossier annuel avec :</Paragraph>
          <BulletList>
            <li>Calendrier d&apos;entretien (chauffage, ventilation, toiture, espaces communs et sécurité).</li>
            <li>Rapports d'inspection signés par vos fournisseurs locaux.</li>
            <li>Photos avant/après travaux et factures numérisées.</li>
            <li>Communication envoyée aux locataires (avis d'entretien, suivis de requêtes).</li>
          </BulletList>

          <Paragraph>
            En centralisant ces informations, vous simplifiez les échanges avec la municipalité, l’assureur, les fournisseurs et les occupants.
            Revoyez le dossier au moins une fois par année et chaque fois qu’un règlement ou l’usage de l’immeuble change.
          </Paragraph>
        </Article>
      </main>
      <Footer />
    </PageWrapper>
  )
}

export default ReglementationImmeublesPortneuf
